// Los hechos de precio que el ARMADOR DEL CLIENTE necesita para no discrepar con la
// caja: la rejilla precio × tamaño del local y el modo de precio por zona.
//
// Por qué existe este archivo y no dos conversiones sueltas. El microsite y el kiosko
// cargan la rejilla por su cuenta, cada uno con su consulta. Si cada uno la
// convirtiera a Map a su manera, tendríamos dos formas de indexar la misma tabla — y
// la que se equivocara caería silenciosamente al precio plano, que es exactamente el
// fallo que esto viene a cerrar (D10 no distingue "no hay celda" de "la busqué mal").
//
// La clave la produce `sizeGridKey`, la misma del motor del servidor. No se
// reimplementa acá a propósito.

import { sizeGridKey, type SizePriceCell } from "@/lib/menu/zonedQuote"
import type { ZonedPricingMode } from "@/lib/menu/zonedPricing"

export type ZonedClientFacts = {
  /** `optionId|sizeOptionId` → celda. Vacío es válido: todo cae al precio plano. */
  grid: Map<string, SizePriceCell>
  /** Media pizza cobra la mitad (`prorated`) o el precio entero (`full`). */
  mode: ZonedPricingMode
}

/** Hechos neutros: sin rejilla y prorrateado. Es lo que aplica a un menú sin pizzas. */
export const NO_ZONED_FACTS: ZonedClientFacts = { grid: new Map(), mode: "prorated" }

/**
 * Convierte lo que viaja del servidor al cliente (un objeto plano, serializable) en
 * los hechos que consume el armador.
 *
 * El `Record` es la forma que cruza el límite servidor→cliente: un `Map` no
 * sobrevive la serialización de React Server Components. Por eso la conversión ocurre
 * acá, del lado del cliente, y no en el cargador.
 */
export function zonedFactsFrom(
  sizeGrid: Record<string, number> | null | undefined,
  mode: string | null | undefined,
): ZonedClientFacts {
  const grid = new Map<string, SizePriceCell>()
  for (const [clave, valor] of Object.entries(sizeGrid ?? {})) {
    // Se descarta lo que no sea número utilizable en vez de dejarlo pasar: una celda
    // con NaN pintaría "$NaN" y, peor, haría que el total dejara de ser un número.
    if (!Number.isFinite(valor)) continue
    const sep = clave.indexOf("|")
    if (sep <= 0) continue
    const optionId = clave.slice(0, sep)
    const sizeOptionId = clave.slice(sep + 1)
    if (!sizeOptionId) continue
    grid.set(sizeGridKey(optionId, sizeOptionId), {
      option_id: optionId,
      size_option_id: sizeOptionId,
      price_delta: valor,
    })
  }
  // Cualquier cosa que no sea exactamente "full" cae a prorrateado, que es el
  // comportamiento de los 43 locales de hoy y el default de la columna.
  return { grid, mode: mode === "full" ? "full" : "prorated" }
}
